import moment from "moment";

import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { CommunicationsUIState, CommunicationsUIStateInbox, RecallStatusTypes, SortDirection } from "@/Common/Models/UI/Enums";
import { CheckboxTarget } from "@/Common/Models/UI/Inputs/CheckboxTarget";
import { CheckboxUIModel } from "@/Common/Models/UI/Inputs/CheckboxUIModel";
import { KeyValuePair } from "@/Common/Models/UI/KeyValuePair";
import { IJRecallPopupModel } from "@/Communications/Models/JSONInterfaces/IJRecallItemPopupModel";
import { MessageOrTaskItem } from "@/Communications/Models/MessageOrTaskItem";
import { TaskGroupType } from "@/Communications/Models/Tasks/Enums/TaskGroupType";
import { IJCommunicationsPopupModel } from "../Models/JSONInterfaces/IJCommunicationsPopupModel";
import { IJMessageOrTaskItemModel } from "../Models/JSONInterfaces/IJMessageOrTaskItemModel";
import { IJMessageTaskSearchModel } from "../Models/JSONInterfaces/IJMessageTaskSearchModel";
import { IJRecallItemModel } from "../Models/JSONInterfaces/IJRecallItemModel";
import { IJTaskMessagePopupModel } from "../Models/JSONInterfaces/IJTaskMessagePopupModel";

class CommunicationsHelper {
    public static GetDefaultMessageOrTaskItemModel(uiState: CommunicationsUIState): IJMessageOrTaskItemModel {
        return {
            IM_ID: "",
            Model: new MessageOrTaskItem(),
            ShowAddNoteControl: false,
            SelectedClinicians: [],
            Priorities: [],
            SelectedPriorityId: "",
            SelectedPatients: [],
            Subject: "",
            Body: "",
            ShowVacationPopup: false,
            EmployeesInVacation: [],
            AttachedDocuments: [],
            AttachedLab: "",
            SharedInboxClinicians: [],
            SelectedGroupTypeId: "",
            SelectedTaskTypeId: "",
            SelectedStatusId: "",
            StartDate: null,
            DueDate: null,
            NotesPopupOpen: false,
            IsPopupForMessages: false,
            NotesPopupLeft: 0,
            NotesPopupTop: 0,
            CurrentMessageNotes: [],
            MessageStatusId: "",
            MessageId: "",
            SendToArchiveSharedInbox: false,
            SenderView: uiState,
            TargetElement: null,
            ShowClickableTextBody: false,
            ShowClickableTextSubject: false
        };
    }

    public static GetDefaultMessageTaskSearchModel(): IJMessageTaskSearchModel {
        return {
            UIState: CommunicationsUIStateInbox.Inbox,
            SearchText: "",
            IsTaskSearch: false,
            IsSelectAllChecked: false,
            IsSearchSettingsOpen: false,
            IsColumnOrderingPopupOpen: false,
            IsAllSitesChecked: false,
            IsAllCliniciansChecked: false,
            IsAllStatusesChecked: false,
            IsAllPrioritiesChecked: false,
            IsAllSharedWithChecked: false,
            CliniciansSearchText: "",
            CliniciansSharedSearchText: "",
            CliniciansFiltered: [],
            CliniciansGlobalCache: [],
            CheckedSites: [],
            CheckedStatuses: [],
            CheckedPrioritiesMessage: [],
            CheckedPrioritiesTask: [],
            CheckedSharedWithMessage: [],
            SharedWithEmployeeList: [],
            CheckedSharedWithTask: [],
            DateFromMessage: null,
            DateToMessage: null,
            DateFromTask: null,
            DateToTask: null,
            SelectedPageSizeOption: 50,
            SelectedPageNumber: 1,
            PageSizeOptions: [50, 100, 150],
            CurrentPage: 0,
            TotalQueryPages: 0,
            TotalQueryRows: 0,
            OrderByColumn: 0,
            OrderByDirection: SortDirection.ASC,
            TableData: [],
            IsModelReady: false
        };
    }

    public static GetDefaultCommunicationsPopupModel(): IJCommunicationsPopupModel {
        return {
            Clinicians: [],
            IsVacationResponderPopupVisible: false,
            PopupTarget: 0,
            SelectedGroup: null,
            OperationToDeleteEmptyGroupFromClinicianSelection: 0
        };
    }

    public static GetDefaultRecallItemModel(): IJRecallItemModel {
        return {
            IsModelValid: true,
            SelectedPatients: [],
            SelectedDescriptions: [],
            DescriptionText: "",
            SelectedClinicians: [],
            CliniciansGlobalCache: [],
            CliniciansSearchText: "",
            SelectedSpecializationId: "",
            Specializations: [],
            DueDate: null,
            SelectedPriorityId: "",
            Priorities: [],
            Notes: "",
            IsSaving: false,
            RecallId: "",
            ResponsibleProviderId: "",
            ResponsibleProviderName: "",
            CreatedByName: "",
            SelectedStatusId: "",
            Statuses: [],
            Communications: [],
            CommunicationNote: "",
            ShowClinicianPopupMenu: false,
            Model: null,
            SenderView: CommunicationsUIState.RecallsSearch
        };
    }

    public static GetDefaultTaskMessagePopupModel(): IJTaskMessagePopupModel {
        var clinicians = cacheStore.CliniciansAndEmployeeGroups;
        var types = cacheStore.FindLookupsByTypeCode("TaskType");
        var priorities = [];
        priorities.push(cacheStore.FindLookupByLookupCode("Low", "TaskPrority"));
        priorities.push(cacheStore.FindLookupByLookupCode("Normal", "TaskPrority"));
        priorities.push(cacheStore.FindLookupByLookupCode("Hight", "TaskPrority"));

        var taskTypes = CommunicationsHelper.GetTaskTypes();

        var model: IJTaskMessagePopupModel =
            {
                IsFolderChecked: true,
                Clinicians: Immutable.List(clinicians),
                Patients: Immutable.List([]),
                Types: Immutable.List(types),
                Priorities: Immutable.List(priorities),
                TaskTypes: Immutable.List(taskTypes),
                StartDate: null,
                EndDate: null,
                Body: "",
                Subject: "",
                SelectedTypeOfTask: taskTypes[0].Key,
                SelectedType: types[0].LookupID,
                SelectedPriorityId: cacheStore.FindLookupByLookupCode("Normal", "TaskPrority").LookupID,
                SelectedClinicians: Immutable.List([]),
                SelectedPatients: Immutable.List([]),
                IsPopupVisible: false,
                TempDocument: null,
                IsTask: false,
                TempAttachedLabFillerID: "",
                SubTitle: "",
                Title: ""
            };

        return model;
    }

    public static GetDefaultRecallPopupModel(): IJRecallPopupModel {
        var clinicianPersons = cacheStore.GetCliniciansAsPersons();
        var prioritiesLookups = CommunicationsHelper.GetRecallPrioritiesLookupsFromCache();
        var specializations = CommunicationsHelper.GetRecallSpecializationsFromCache();
        var defaultPriority = cacheStore.FindLookupByLookupCode("PMedium");

        var model: IJRecallPopupModel =
            {
                RecallPopupId: sessionStore.newId().toString(),
                IsPopupOpen: false,
                IsModelValid: true,
                SelectedPatients: Immutable.List([]),
                SelectedDescriptions: Immutable.List([]),
                CliniciansGlobalCache: Immutable.List(clinicianPersons),
                SelectedClinicians: Immutable.List([]),
                DescriptionText: "",
                SelectedSpecializationId: "",
                Specializations: Immutable.List(specializations),
                DueDate: null,
                SelectedPriorityId: defaultPriority.LookupID,
                Priorities: Immutable.List(prioritiesLookups),
                Notes: "",
                RecallId: "",
                ResponsibleProviderId: "",
                ResponsibleProviderName: "",
                CreatedByName: "",
                SelectedStatusId: "",
                Statuses: Immutable.List([]),
                Communications: Immutable.List([]),
                CommunicationNote: "",
                CliniciansSearchText: "",
                Model: null,
                ShowClinicianPopupMenu: false,
                IsSaving: false

            };

        return model;
    }

    public static GetTaskTypes(): KeyValuePair<TaskGroupType, string>[] {
        return [
            new KeyValuePair(TaskGroupType.group, cacheStore.GetString("GroupTaskLabel")),
            new KeyValuePair(TaskGroupType.individual, cacheStore.GetString("IndividualTaskLabel"))
        ];
    }

    public static GetCommunicationPrioritiesCheckboxesFromCache(lookupType: string, target: CheckboxTarget): CheckboxUIModel[] {
        let priorityLookups = cacheStore.FindLookupsByTypeCode(lookupType);

        if (!priorityLookups) return null;

        return priorityLookups.map(x => new CheckboxUIModel({
            Id: x.LookupID,
            Name: cacheStore.GetLocalizedValueByLookupId(x.LookupID),
            Target: target,
            Checked: true
        }));
    }

    public static GetRecallPrioritiesLookupsFromCache(): FlatLookupView[] {
        var priorityLookups = cacheStore.FindLookupsByTypeCode("PriorityType");

        return priorityLookups;
    }

    public static GetRecallStatusesLookupsFromCache(): FlatLookupView[] {
        var priorityLookups = cacheStore.FindLookupsByTypeCode("RecallStatusType");

        priorityLookups = ArrayHelper.SortDynamic(priorityLookups, "LookupPosition");

        return priorityLookups;
    }

    public static GetRecallStatusesFromCache(): CheckboxUIModel[] {
        var statuses: CheckboxUIModel[] = [];
        var pastDueStatus = new CheckboxUIModel();
        pastDueStatus.Id = RecallStatusTypes.PastDue.toString();
        pastDueStatus.Target = CheckboxTarget.Myle_Communications_Recalls_Search_Status;
        pastDueStatus.Name = cacheStore.GetString("PastDueLabel");
        pastDueStatus.Checked = true;
        statuses.push(pastDueStatus);
        var dueStatus = new CheckboxUIModel();
        dueStatus.Id = RecallStatusTypes.Due.toString();
        dueStatus.Target = CheckboxTarget.Myle_Communications_Recalls_Search_Status;
        dueStatus.Name = cacheStore.GetString("DueLabel");
        dueStatus.Checked = true;
        statuses.push(dueStatus);
        var completedStatus = new CheckboxUIModel();
        completedStatus.Id = RecallStatusTypes.Completed.toString();
        completedStatus.Target = CheckboxTarget.Myle_Communications_Recalls_Search_Status;
        completedStatus.Name = cacheStore.GetString("CompletedLabel");
        completedStatus.Checked = false;
        statuses.push(completedStatus);

        return statuses;
    }

    public static GetRecallSpecializationsFromCache(): FlatLookupView[] {
        var result: FlatLookupView[] = [];

        var specs = cacheStore.FindLookupsByTypeCode("Specialization_def");

        var flatLookup = new FlatLookupView();
        flatLookup.LookupID = "null";
        flatLookup.en = "";
        flatLookup.fr = "";
        flatLookup.enLocalizedValueID = "";
        flatLookup.frLocalizedValueID = "";
        result.push(flatLookup);
        specs
            .sort((a, b) => {
                if (a.LookupPosition < b.LookupPosition) return -1;
                if (a.LookupPosition > b.LookupPosition) return -1;
                return 0;
            })
            .forEach(e => result.push(e));
        return result;
    }

    public static determineOverallStatusColor(overallStatusLookupId: string, dueDate?: Date): string {
        if (!overallStatusLookupId)
            return "";

        let completedLookupId = cacheStore.FindLookupByLookupCode("Completed", "TaskStatus");
        if (overallStatusLookupId.toLowerCase() === completedLookupId.LookupID.toLowerCase())
            return "green";

        if (dueDate && moment() > moment(dueDate))
            return "red";

        return "";
    }

    /**
     * Return the label used as a key for the getText hook by communicationsUIStateInbox
     * @param communicationsUIStateInbox
     */
    public static getLabelByCommunicationsUIStateInbox(communicationsUIStateInbox: CommunicationsUIStateInbox): string {
        switch (communicationsUIStateInbox) {
            case CommunicationsUIStateInbox.Inbox:
                return "InboxLabel";
            case CommunicationsUIStateInbox.Sent:
                return "SentFLabel";
            case CommunicationsUIStateInbox.Drafts:
                return "DraftsLabel";
            case CommunicationsUIStateInbox.Trash:
                return "TrashLabel";
            case CommunicationsUIStateInbox.Archive:
                return "ArchiveLabel";
            case CommunicationsUIStateInbox.FlaggedForReview:
                return "SharedItem_FlaggedForReviewLabel";
            default:
                return "";
        }
    }

    /**
     * Check if the inbox type is Sent.
     * @param communicationsUIStateInbox The inbox type in the communication tab.
     * @returns True if the inbox type is Sent.
     */
    public static isInboxTypeSent(communicationsUIStateInbox: CommunicationsUIStateInbox): boolean {
        return communicationsUIStateInbox === CommunicationsUIStateInbox.Sent;
    }
}

export { CommunicationsHelper };
